import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  ArrowRight,
  Rocket,
  Upload,
  Search,
  CheckSquare,
  Clock,
  Users,
  Link2
} from 'lucide-react';

interface Guide {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  to: string;
  roles: string[];
  sections: string[];
}

const guides: Guide[] = [
  {
    id: 'onboarding',
    title: 'Website Onboarding Guide',
    description: 'Complete walkthrough for onboarding a new website from scratch to AEO-ready content. Covers sector setup, site creation, feed import, research, and content generation.',
    icon: Rocket,
    to: '/help/guides/onboarding',
    roles: ['Super Admin', 'Content Manager'],
    sections: [
      'Sector Setup',
      'Site Creation',
      'Feed Import',
      'Category Research',
      'AEO Generation',
      'Discovery Verification'
    ]
  },
  {
    id: 'feed-import',
    title: 'Feed Import Guide',
    description: 'How to prepare, upload, configure, and process product feeds. Includes feed format requirements, enrichment options, and troubleshooting tips.',
    icon: Upload,
    to: '#feed-import',
    roles: ['Content Manager'],
    sections: [
      'Feed Formats',
      'Upload Process',
      'Enrichment Options',
      'Scheduling',
      'Monitoring Progress'
    ]
  },
  {
    id: 'category-research',
    title: 'Category Research Guide',
    description: 'Understanding and running category research for better AEO content. Learn how to discover perceived benefits, attribute intelligence, and personas.',
    icon: Search,
    to: '/help/guides/category-research',
    roles: ['Super Admin', 'Content Manager'],
    sections: [
      'Perceived Benefits',
      'Attribute Intelligence',
      'Persona Discovery',
      'Price Tiers',
      'Use Cases'
    ]
  },
  {
    id: 'google-integrations',
    title: 'Connecting Google Services',
    description: 'How to connect Google Search Console, Analytics 4, Ads, and Merchant Center to PageMind. Step-by-step walkthrough with screenshots of the full OAuth flow.',
    icon: Link2,
    to: '/help/guides/google-integrations',
    roles: ['Content Manager', 'Super Admin'],
    sections: [
      'Service Selection',
      'Google OAuth Flow',
      'Permission Checkboxes',
      'Verify Connection',
      'Configure Properties'
    ]
  },
  {
    id: 'content-review',
    title: 'Content Review Guide',
    description: 'How to review, edit, and approve AI-generated content. Covers the review workflow, quality indicators, and handling flagged content.',
    icon: CheckSquare,
    to: '#content-review',
    roles: ['Content Manager'],
    sections: [
      'Review Workflow',
      'Quality Indicators',
      'Editing Content',
      'Bulk Operations',
      'Approval Process'
    ]
  }
];

export default function UserGuides() {
  return (
    <div className="container mx-auto p-6 max-w-5xl">
      <div className="mb-6">
        <Link
          to="/help"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Help Center
        </Link>
        <h1 className="text-3xl font-bold mb-2">User Guides</h1>
        <p className="text-muted-foreground">
          Step-by-step guides for getting the most out of PageMind
        </p>
      </div>

      <div className="space-y-4">
        {guides.map((guide) => {
          const isAvailable = guide.to.startsWith('/');

          return (
            <Card
              key={guide.id}
              id={guide.id}
              className={isAvailable ? 'hover:border-primary/50 transition-colors' : 'opacity-75'}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <guide.icon className="h-6 w-6 text-primary mt-1" />
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {guide.title}
                        {!isAvailable && (
                          <Badge variant="outline" className="ml-2">Coming Soon</Badge>
                        )}
                      </CardTitle>
                      <CardDescription className="mt-1">{guide.description}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Users className="h-4 w-4" />
                    <span>For:</span>
                    {guide.roles.map((role, idx) => (
                      <Badge key={role} variant="secondary" className="ml-1">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium mb-2">Covers:</p>
                  <div className="flex flex-wrap gap-2">
                    {guide.sections.map((section) => (
                      <Badge key={section} variant="outline">
                        {section}
                      </Badge>
                    ))}
                  </div>
                </div>

                {isAvailable ? (
                  <Link
                    to={guide.to}
                    className="inline-flex items-center text-sm text-primary font-medium hover:underline"
                  >
                    Read guide
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Link>
                ) : (
                  <span className="inline-flex items-center text-sm text-muted-foreground">
                    <Clock className="h-4 w-4 mr-1" />
                    Guide in development
                  </span>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="mt-8 bg-muted/50">
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">
            Need help with something not covered here?{' '}
            <Link to="/help/faq" className="text-primary hover:underline">
              Check our FAQ
            </Link>{' '}
            or{' '}
            <Link to="/my-suggestions" className="text-primary hover:underline">
              submit a suggestion
            </Link>{' '}
            for a new guide topic.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
